import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type UserRoleType = Database['public']['Enums']['user_role_type'];

export interface RoleInfo {
  code: string;
  display_name: string;
  description: string | null;
}

export async function getRoleDescriptions(): Promise<{
  success: boolean;
  data: RoleInfo[] | null;
  error?: unknown;
}> {
  try {
    const { data, error } = await supabase
      .from('roles')
      .select('code, display_name, description');

    if (error) {
      console.error('Error fetching role descriptions:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data ?? [] };
  } catch (error) {
    console.error('Error in getRoleDescriptions:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Get all roles for a user
 */
export async function getUserRoles(userId: string): Promise<{ success: boolean; data: UserRoleType[] | null; error?: any }> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user roles:', error);
      return { success: false, data: null, error };
    }

    const roles = data?.map(r => r.role) || [];
    return { success: true, data: roles };
  } catch (error) {
    console.error('Error in getUserRoles:', error);
    return { success: false, data: null, error };
  }
}

// Priority order for displaying a user's primary staff role
const ROLE_PRIORITY: UserRoleType[] = [
  'super_admin', 'admin', 'ops', 'directeur_manager',
  'concierge', 'ops_assistant', 'pbm', 'realisateur',
  'virality_expert', 'monteur', 'derusher', 'cm', 'csm', 'miniamaker', 'tech', 'customer'
];

/**
 * Get the display name of a user's primary (highest-priority) role
 */
export async function getUserPrimaryRoleDisplayName(userId: string): Promise<{ success: boolean; data: string | null; error?: any }> {
  try {
    const [rolesResult, descriptionsResult] = await Promise.all([
      getUserRoles(userId),
      getRoleDescriptions()
    ]);

    if (!rolesResult.success || !rolesResult.data || rolesResult.data.length === 0) {
      return { success: true, data: null };
    }

    const roles = rolesResult.data;
    const descMap = new Map((descriptionsResult.data || []).map(r => [r.code, r.display_name]));

    // Find the highest-priority non-customer role
    const primaryRole = ROLE_PRIORITY.find(r => r !== 'customer' && roles.includes(r))
      || (roles.includes('customer') ? 'customer' : roles[0]);

    return { success: true, data: descMap.get(primaryRole) || primaryRole };
  } catch (error) {
    console.error('Error in getUserPrimaryRoleDisplayName:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Update a role's display_name and/or description
 */
export async function updateRoleInfo(
  code: string,
  updates: { display_name?: string; description?: string | null }
): Promise<{ success: boolean; error?: unknown }> {
  try {
    const { error } = await supabase
      .from('roles')
      .update(updates)
      .eq('code', code as UserRoleType);

    if (error) {
      console.error('Error updating role info:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateRoleInfo:', error);
    return { success: false, error };
  }
}

/**
 * Check if the user is staff (any staff role: admin, super_admin, pbm, ops, etc.) via database RPC.
 */
export async function isStaff(userId: string): Promise<{ success: boolean; data: boolean; error?: unknown }> {
  try {
    const { data, error } = await supabase.rpc('is_staff', { _user_id: userId });
    if (error) {
      console.error('Error checking is_staff:', error);
      return { success: false, data: false, error };
    }
    return { success: true, data: data === true };
  } catch (error) {
    console.error('Error in isStaff:', error);
    return { success: false, data: false, error };
  }
}

/**
 * Check if a user has a specific role using the database RPC function
 */
export async function hasRole(userId: string, role: UserRoleType): Promise<{ success: boolean; data: boolean; error?: any }> {
  try {
    // Note: The RPC function signature expects _role first, then _user_id
    const { data, error } = await supabase
      .rpc('has_role', { _role: role, _user_id: userId });

    if (error) {
      console.error('Error checking role:', error);
      return { success: false, data: false, error };
    }

    return { success: true, data: !!data };
  } catch (error) {
    console.error('Error in hasRole:', error);
    return { success: false, data: false, error };
  }
}

export interface RolePermission {
  service: string;
  has_access: boolean;
  role: UserRoleType;
}

export interface RolePermissionWithId {
  id: string;
  role: UserRoleType;
  service: string;
  has_access: boolean;
}

/**
 * Get permissions for multiple roles
 */
export async function getRolePermissions(roles: UserRoleType[]): Promise<{
  success: boolean;
  data: RolePermission[] | null;
  error?: any;
}> {
  try {
    if (roles.length === 0) {
      return { success: true, data: [] };
    }

    const { data, error } = await supabase
      .from('role_permissions')
      .select('service, has_access, role')
      .in('role', roles);

    if (error) {
      console.error('Error fetching permissions:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error in getRolePermissions:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Get all role permissions (admin function)
 */
export async function getAllRolePermissions(): Promise<{
  success: boolean;
  data: RolePermissionWithId[] | null;
  error?: any;
}> {
  try {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('*')
      .order('service')
      .order('role');

    if (error) {
      console.error('Error fetching all permissions:', error);
      return { success: false, data: null, error };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error in getAllRolePermissions:', error);
    return { success: false, data: null, error };
  }
}

/**
 * Update a role permission
 */
export async function updateRolePermission(
  permissionId: string,
  hasAccess: boolean
): Promise<{
  success: boolean;
  error?: any;
}> {
  try {
    const { error } = await supabase
      .from('role_permissions')
      .update({ has_access: hasAccess })
      .eq('id', permissionId);

    if (error) {
      console.error('Error updating permission:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateRolePermission:', error);
    return { success: false, error };
  }
}

/**
 * Check if a user has access to a specific service using the database RPC function
 */
export async function hasServiceAccess(
  userId: string,
  service: string
): Promise<{
  success: boolean;
  data?: boolean;
  error?: any;
}> {
  try {
    const { data, error } = await supabase.rpc('has_service_access', {
      _user_id: userId,
      _service: service,
    });

    if (error) {
      console.error('Error checking service access:', error);
      return { success: false, error };
    }

    return { success: true, data: !!data };
  } catch (error) {
    console.error('Error in hasServiceAccess:', error);
    return { success: false, error };
  }
}
